/** Attributed campaigns as a `Timeline` (L1), drilling into campaign detail (L2).
 *
 *  A sequence of events is a `Timeline` (dashboard design rules §2). This is the
 *  source of the deck's "Iran- and China-themed lures both observed this
 *  quarter" line, and the panel that names an attacker rather than an employee.
 *
 *  Each row is a door into an L2 `DetailDrawer` carrying actors, malware
 *  families and volume, with the open campaign held in the URL (`?campaign=<id>`)
 *  so the view is shareable and the back button closes it.
 */

import { Badge, EmptyState, Timeline, Typography } from "@norican/react";

import { DetailDrawer, type Verdict } from "@/components/board";
import type { SecurityCampaignRow } from "@/lib/api";
import { useDrawerParam } from "@/lib/drill";

import { CAMPAIGN_ANCHOR } from "./exec-tiles";
import { PanelCard } from "./panel-card";
import { formatCount, formatTimestamp } from "./status";

/** `?campaign=<id>` opens one campaign's L2 drawer. */
const CAMPAIGN_PARAM = "campaign";

interface CampaignTimelineProps {
  campaigns: SecurityCampaignRow[];
  /** Every distinct actor across the window — the attribution headline. */
  actors: string[];
  windowDays: number;
}

export function CampaignTimeline({ campaigns, actors, windowDays }: CampaignTimelineProps) {
  const drawer = useDrawerParam(CAMPAIGN_PARAM);
  const open = campaigns.find((campaign) => campaign.id === drawer.value);

  return (
    <section id={CAMPAIGN_ANCHOR} className="scroll-mt-6">
      <PanelCard
        title="Attributed campaigns"
        caption={`Last ${windowDays} days · attribution is Proofpoint's, and names an actor group rather than a state`}
        verdict={campaignVerdict(campaigns.length, actors)}
      >
        {campaigns.length === 0 ? (
          <EmptyState
            size="sm"
            title="No campaigns recorded"
            description="The once-daily campaign batch has not returned an attributed campaign in this window."
          />
        ) : (
          <div className="flex flex-col gap-4">
            {actors.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {actors.map((actor) => (
                  <Badge key={actor} tone="warning" variant="subtle" size="sm">
                    {actor}
                  </Badge>
                ))}
              </div>
            )}
            <Timeline
              items={campaigns.map((campaign) => ({
                id: campaign.id,
                tone: "warning" as const,
                timestamp: formatTimestamp(campaign.first_seen),
                title: (
                  <button
                    type="button"
                    className="text-fg hover:text-accent text-left font-medium underline-offset-2 hover:underline"
                    onClick={() => drawer.open(campaign.id)}
                  >
                    {campaign.name || campaign.id}
                  </button>
                ),
                description: (
                  <span className="text-fg-muted text-sm">
                    {formatCount(campaign.volume)} messages
                    {campaign.actors.length > 0 && ` · ${campaign.actors.join(", ")}`}
                    {campaign.families.length > 0 && ` · ${campaign.families.join(", ")}`}
                  </span>
                ),
              }))}
            />
          </div>
        )}
      </PanelCard>

      <DetailDrawer
        open={open != null}
        onClose={drawer.close}
        title={open?.name || open?.id || "Campaign"}
        breadcrumbs={[{ label: "Security", to: "/security" }, { label: "Campaigns" }]}
        items={
          open
            ? [
                { label: "Campaign id", value: open.id },
                { label: "First seen", value: formatTimestamp(open.first_seen) },
                { label: "Messages", value: formatCount(open.volume) },
                { label: "Actors", value: open.actors.join(", ") || "Not attributed" },
                { label: "Malware families", value: open.families.join(", ") || "None recorded" },
                { label: "Malware", value: open.malware.join(", ") || "None recorded" },
              ]
            : undefined
        }
      >
        {/* The absence below is deliberate and is stated rather than left to be
            noticed: who a given campaign was addressed to is not stored. Who is
            being targeted overall is answered by the panel above this one, from
            the People API, which is a count of individuals rather than a
            message-level log of everyone. */}
        <Typography variant="body-sm" tone="muted">
          Campaign membership — which mailboxes each message was addressed to — is not stored and is
          not shown. Proofpoint returns it, and the collector discards it before the first write.
          Message-level investigation belongs in the TAP console, which has its own audit trail.
        </Typography>
      </DetailDrawer>
    </section>
  );
}

/** What the attribution means, rather than how many rows there are. */
function campaignVerdict(count: number, actors: string[]): Verdict {
  if (count === 0) {
    return {
      label: "Quiet",
      tone: "neutral",
      text: "No attributed campaign in this window. Absence of attribution is not absence of attack — the volume panels above are the ones that measure pressure.",
    };
  }
  if (actors.length === 0) {
    return {
      label: "Watch",
      tone: "warning",
      text: `${count} campaign${count === 1 ? "" : "s"} recorded, none attributed to a named actor group.`,
    };
  }
  return {
    label: "Watch",
    tone: "warning",
    text: `${count} campaign${count === 1 ? "" : "s"} from ${actors.length} named actor group${
      actors.length === 1 ? "" : "s"
    } — this is the line the deck reports as targeted, not opportunistic.`,
  };
}
